import { useState, useEffect } from 'react';
import { Terminal, Save, X, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { apiClient } from '../../lib/api-client';
import { CliPermissionsTree } from './CliPermissionsTree';

interface CliSchema {
  version: string;
  lastUpdated: string;
  cli_command_groups: Record<string, any>;
}

interface CliGroupPermissions {
  description: string;
  permissions: Record<string, any>;
}

interface CliPermissionsDialogProps {
  open: boolean;
  onClose: () => void;
  groupName: string;
  onSaved?: () => void;
}

export function CliPermissionsDialog({
  open,
  onClose,
  groupName,
  onSaved,
}: CliPermissionsDialogProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [schema, setSchema] = useState<CliSchema | null>(null);
  const [permissions, setPermissions] = useState<Record<string, any>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, groupName]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load schema and permissions in parallel
      const [schemaData, permsData] = await Promise.all([
        apiClient.getCliSchema(),
        apiClient.getGroupCliPermissions(groupName),
      ]);

      setSchema(schemaData);
      setPermissions(permsData.permissions || {});
      setHasChanges(false);
    } catch (err: any) {
      console.error('Failed to load CLI permissions:', err);
      setError(err.message || 'Failed to load CLI permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (newPermissions: Record<string, any>) => {
    setPermissions(newPermissions);
    setHasChanges(true);
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSyncSuccess(false);
    try {
      // Sync CLI schema from Git (invalidates cache and fetches latest)
      const schemaData = await apiClient.syncCliSchema();
      setSchema(schemaData);
      
      // Show success indicator
      setSyncSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSyncSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.error('Failed to sync CLI schema:', err);
      setError(err.message || 'Failed to sync CLI schema');
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.updateGroupCliPermissions(groupName, {
        description: `CLI permissions for ${groupName}`,
        permissions,
      });

      setHasChanges(false);
      onSaved?.();
      onClose();
    } catch (err: any) {
      console.error('Failed to save CLI permissions:', err);
      setError(err.message || 'Failed to save CLI permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent maxWidth="3xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-blue-500" />
            CLI Permissions: {groupName}
            {hasChanges && (
              <span className="text-xs text-amber-600 dark:text-amber-400 font-normal">
                (Unsaved changes)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : schema ? (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Configure which CLI commands this group can execute. Use "All" checkboxes for convenient bulk permissions.
                </p>
              </div>

              {/* Sync button and success message */}
              <div className="flex justify-end items-center gap-3">
                {syncSuccess && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">CLI commands synced successfully!</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={syncing}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync CLI Commands'}
                </Button>
              </div>

              <CliPermissionsTree
                schema={schema}
                permissions={permissions}
                onChange={handlePermissionChange}
              />
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
